import moment from 'moment';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { fetchCurrentHoldingDetailsVT2 } from 'views/Portfolio/components/Details/services/fetchCurrentHoldings';
import { OffersType, StocksType } from 'views/Trade/types';
import useFetchPortalDocument from 'hooks/useFetchPortalDocument';
import { useIsProspect } from 'hooks/useIsProspect';
import { PortalDocumentTypeEnum } from 'types/serviceTypes';
import ProspectTradingNotice from '../../ProspectTradingNotice';
import { buildDisplayText, classNames, roundNumber } from '../../../utils';
import { Product } from '../../../types/productType';
import Button from '../../Button';
import LiveExPerformanceChart from './LiveExPerformanceChart';
import QualityRadarChart from '../../../views/MyCellar/components/QualityRadarChart';
import cutlWinesLoversImage from '../../../assets/images/cult_wines_lovers_image.png';
import { BuySellHoldingModel, DisplayTextKeys, PricingType, ProductEventType, ViewStateType } from '../types';
import SellHoldingTemplate from './SellHoldingTemplate';
import InvestMoreTemplate from './InvestMoreTemplate';
import ArrangeDeliveryTemplate, { blankAddessModel } from './ArrangeDelivery';
import { SortAndFilterLayoutContext } from '../../Layout/SortAndFilterLayout';
import { AppContext } from '../../../context/ContextProvider';
import { ArrangeDeliveryRequest } from '../services/arrangeDeliveryApi';

interface SellOrInvestProductTemplateProps {
  product: Product;
  timestamp?: number;
  onCTA?: <T>(event: ProductEventType, data: T) => void;
  isDetailsFetched?: boolean;
  isLoadingMarket: boolean;
  isLoadingPortfolioStock: boolean;
  marketOffers: OffersType[] | null;
  portfolioStock: StocksType | null;
  isMetricsLoading?: boolean; // External loading state for quality metrics
}

// enum StockStatusEnum {
//   IN_TRANSIT = 'intransit',
// }
// const notApplicable = 'n/a';

enum InfoColors {
  NONE = 'transparent',
  MARKET = '#1D4854',
  P_AND_L = '#FF906D',
  COST = '#55A2A7',
  FEES = '#B1DED8',
}

const defaultModel = {
  price: 0,
  units: 0,
  reason: '',
  pricingType: PricingType.MARKET,
};
interface MarketInfoGraphProps {
  includeFees: boolean;
  data: {
    marketValue: number;
    pNl: number;
    fees: number;
    pNlIncludeFees: number;
  };
}

const MarketInfoGraph: FC<MarketInfoGraphProps> = ({ includeFees, data }) => {
  const plValue = Math.ceil(((includeFees ? data.pNlIncludeFees : data.pNl) / data.marketValue) * 100);
  const fees = Math.ceil((data.fees / data.marketValue) * 100);
  const feesWidth = `${fees}%`;
  const plWidth = `${Math.max(0, plValue)}%`;
  const costWidth = `${Math.max(0, 100 - ((includeFees ? fees : 0) + plValue))}%`;
  if (!data.marketValue) return null;

  return (
    <div className="flex flex-col w-full rounded-lg overflow-hidden shadow-sm">
      <div className="w-full h-[12px] rounded-t-lg" style={{ background: InfoColors.MARKET }} />
      <div className="w-full flex">
        <div className="h-[12px]" style={{ background: InfoColors.P_AND_L, width: plWidth }} />
        {includeFees && <div className="h-[12px]" style={{ background: InfoColors.FEES, width: feesWidth }} />}
        <div className="h-[12px] rounded-br-lg" style={{ background: InfoColors.COST, width: costWidth }} />
      </div>
    </div>
  );
};

/** Section header component */
const SectionHeader: FC<{ title: string; icon?: React.ReactNode }> = ({ title, icon }) => (
  <div className="flex items-center gap-2 mb-3">
    {icon && <span className="text-vine dark:text-teal-300">{icon}</span>}
    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide">{title}</h3>
  </div>
);

/** Stat card component */
const StatCard: FC<{
  label: string;
  value: string;
  subValue?: string;
  color?: string;
  trend?: 'up' | 'down' | 'neutral';
}> = ({ label, value, subValue, color, trend }) => (
  <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 flex-1">
    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</div>
    <div className="flex items-baseline gap-1">
      {color && <div className="w-1 h-5 rounded-full" style={{ background: color }} />}
      <span
        className={`text-base font-semibold ${
          trend === 'up' ? 'text-trendup' : trend === 'down' ? 'text-trenddown' : 'text-gray-900 dark:text-white'
        }`}
      >
        {value}
      </span>
    </div>
    {subValue && <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subValue}</div>}
  </div>
);

// AI Star Icon component
const AiStarIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2L14.09 8.26L21 9.27L16 14.14L17.18 21.02L12 17.77L6.82 21.02L8 14.14L3 9.27L9.91 8.26L12 2Z"
      fill="currentColor"
    />
    <circle cx="19" cy="5" r="2" fill="currentColor" />
    <circle cx="5" cy="19" r="1.5" fill="currentColor" />
  </svg>
);

// Chevron icons for carousel navigation
const ChevronLeftIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
  </svg>
);

// External link icon
// Critic Reviews Carousel Section - matches the design with navigation arrows and source banner
interface TastingNotesSectionProps {
  productDetails: Product;
  t: (key: string) => string;
  isTastingNotesExpanded: boolean;
  setIsTastingNotesExpanded: (value: boolean) => void;
}

const TastingNotesSection: FC<TastingNotesSectionProps> = ({ productDetails, t }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Build reviewer data from product details
  const reviewers = useMemo(() => {
    const items: Array<{
      id: string;
      sourceName: string;
      sourceColor: string;
      textColorClass: string;
      reviewerName: string | null;
      releasePrice: string | null;
      drinkWindow: string | null;
      tastingNote: string | null;
      publishedDate: string | null;
      externalLink: string | null;
    }> = [];

    // Helper to check if tasting note has meaningful content
    const hasValidTastingNote = (note: string | null | undefined): boolean => {
      if (!note) return false;
      const trimmed = note.trim().toLowerCase();
      return trimmed.length > 0 && trimmed !== 'none' && trimmed !== 'n/a' && trimmed !== 'null';
    };

    // Wine Advocate Review - only show if tasting notes are available
    const waReview = productDetails.wineAdvocateReview;
    if (hasValidTastingNote(waReview?.tastingNote)) {
      const drinkWindow =
        waReview?.drinkYearMin && waReview?.drinkYearMax ? `${waReview.drinkYearMin} - ${waReview.drinkYearMax}` : null;
      items.push({
        id: 'wine-advocate',
        sourceName: 'The Wine Advocate',
        sourceColor: '#FCD34D', // amber/yellow - matches CellarWineCard
        textColorClass: 'text-gray-900', // dark text on light background
        reviewerName: waReview?.reviewer || null,
        releasePrice: null,
        drinkWindow,
        tastingNote: waReview?.tastingNote ?? null,
        publishedDate: waReview?.scoreDate ? moment(waReview.scoreDate).format('MMM D, YYYY') : null,
        externalLink: waReview?.articleUrl || null,
      });
    }

    // Vinous Review - only show if tasting notes are available
    const vinousReview = productDetails.vinousReview;
    if (hasValidTastingNote(vinousReview?.tastingNote)) {
      const drinkWindow =
        vinousReview?.drinkFrom && vinousReview?.drinkTo ? `${vinousReview.drinkFrom} - ${vinousReview.drinkTo}` : null;
      items.push({
        id: 'vinous',
        sourceName: 'Vinous',
        sourceColor: '#722F37', // wine red - matches CellarWineCard
        textColorClass: 'text-white', // white text on dark background
        reviewerName: vinousReview?.reviewer || null,
        releasePrice: null,
        drinkWindow,
        tastingNote: vinousReview?.tastingNote ?? null,
        publishedDate: vinousReview?.reviewDate ? moment(vinousReview.reviewDate).format('MMM D, YYYY') : null,
        externalLink: vinousReview?.externalLink || null,
      });
    }

    return items;
  }, [productDetails]);

  // Check if we have any tasting notes content
  const hasTastingNotes = reviewers.length > 0 || productDetails.tastingNotes;

  if (!hasTastingNotes) {
    return null;
  }

  const currentReviewer = reviewers[currentIndex];
  const totalReviewers = reviewers.length;

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalReviewers - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < totalReviewers - 1 ? prev + 1 : 0));
  };

  // Fallback to legacy tasting notes if no reviewer data
  if (reviewers.length === 0 && productDetails.tastingNotes) {
    return (
      <div className="mx-4 my-4 bg-white dark:bg-slate-800 rounded-2xl shadow-sm overflow-hidden border border-gray-100 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-center px-4 py-3 bg-gray-50 dark:bg-slate-700/50">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('product:wineDetails.tastingNotes')}
          </div>
        </div>

        {/* Amber banner */}
        <div className="bg-amber-400 dark:bg-amber-500 px-4 py-2.5">
          <span className="text-sm font-semibold text-gray-900">{t('product:wineDetails.tastingNotes')}</span>
        </div>

        {/* Tasting note text */}
        <div className="px-4 py-4">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed italic">
            {productDetails.tastingNotes}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 my-4 bg-white dark:bg-slate-800 rounded-2xl shadow-sm overflow-hidden border border-gray-100 dark:border-slate-700">
      {/* Header with navigation */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-700/50">
        <div className="flex items-center gap-3">
          {/* Prev button */}
          <button
            onClick={goToPrev}
            disabled={totalReviewers <= 1}
            className="w-8 h-8 rounded-full bg-gray-200 dark:bg-slate-600 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="w-4 h-4 text-gray-700 dark:text-white" />
          </button>

          {/* Title and counter */}
          <div className="text-center">
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('product:wineDetails.criticReviews')}
              {currentReviewer?.id === 'wine-advocate' && productDetails.wa != null && (
                <span className="ml-1 text-gray-500 dark:text-gray-400 font-normal">({productDetails.wa})</span>
              )}
              {currentReviewer?.id === 'vinous' && productDetails.vinous != null && (
                <span className="ml-1 text-gray-500 dark:text-gray-400 font-normal">({productDetails.vinous}v)</span>
              )}
            </div>
            {totalReviewers > 1 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {currentIndex + 1} of {totalReviewers}
              </div>
            )}
          </div>

          {/* Next button */}
          <button
            onClick={goToNext}
            disabled={totalReviewers <= 1}
            className="w-8 h-8 rounded-full bg-gray-200 dark:bg-slate-600 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="w-4 h-4 text-gray-700 dark:text-white" />
          </button>
        </div>
      </div>

      {/* Source banner - dynamic color based on reviewer source */}
      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{ backgroundColor: currentReviewer.sourceColor }}
      >
        <span className={`text-sm font-semibold ${currentReviewer.textColorClass}`}>{currentReviewer.sourceName}</span>
        {currentReviewer.id === 'wine-advocate' && productDetails.wa != null && (
          <span className={`text-sm font-bold ${currentReviewer.textColorClass} bg-white/20 px-2 py-0.5 rounded-full`}>
            {productDetails.wa}
          </span>
        )}
        {currentReviewer.id === 'vinous' && productDetails.vinous != null && (
          <span className={`text-sm font-bold ${currentReviewer.textColorClass} bg-white/20 px-2 py-0.5 rounded-full`}>
            {productDetails.vinous}
          </span>
        )}
      </div>

      {/* Review details */}
      <div className="px-4 py-4">
        {/* Info row - Reviewed by, Release Price, Drink Date */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 italic mb-1">
              {t('product:wineDetails.reviewedBy')}:
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {currentReviewer.reviewerName || 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 italic mb-1">
              {t('product:wineDetails.releasePrice')}:
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {currentReviewer.releasePrice || 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 italic mb-1">
              {t('product:wineDetails.drinkDate')}:
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {currentReviewer.drinkWindow || 'N/A'}
            </div>
          </div>
        </div>

        {/* Tasting note text */}
        {currentReviewer.tastingNote && (
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed italic">
            {currentReviewer.tastingNote}
          </p>
        )}
      </div>

      {/* Footer with published date */}
      <div className="px-4 py-3 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/30">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          <span className="italic">{t('product:wineDetails.published')}:</span>{' '}
          <span className="text-gray-700 dark:text-gray-300">{currentReviewer.publishedDate || 'N/A'}</span>
        </div>
      </div>

      {/* Pagination dots */}
      {totalReviewers > 1 && (
        <div className="flex items-center justify-center gap-2 pb-3 bg-gray-50 dark:bg-slate-700/30">
          {reviewers.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                index === currentIndex
                  ? 'bg-teal-500 dark:bg-teal-400'
                  : 'bg-gray-300 dark:bg-slate-600 hover:bg-gray-400 dark:hover:bg-slate-500'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Market Metrics Section with Radar Chart
interface MarketMetricsSectionProps {
  productDetails: Product;
  currencyFormatter: { format: (value: number, showCurrency?: boolean) => string };
  t: (key: string) => string;
  isLoading?: boolean; // Show loading/disabled state while fetching metrics
}

// Metric Card Component for the Market Metrics section
const MetricCard = ({
  label,
  value,
  suffix,
  highlight,
}: {
  label: string;
  value: string;
  suffix?: string;
  highlight?: boolean;
}) => (
  <div
    className={classNames(
      'rounded-xl p-3',
      highlight ? 'bg-vine/10 dark:bg-teal-500/20' : 'bg-gray-50 dark:bg-slate-700',
    )}
  >
    <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</p>
    <div className="flex items-baseline gap-1">
      <span
        className={classNames(
          'text-base font-bold',
          highlight ? 'text-vine dark:text-teal-400' : 'text-gray-900 dark:text-white',
        )}
      >
        {value}
      </span>
      {suffix && value !== '-' && <span className="text-xs text-gray-400 dark:text-gray-500">{suffix}</span>}
    </div>
  </div>
);

// Valuation Card Component - displays a single holding stock record
interface ValuationItemProps {
  rotationNumber: string;
  location: string;
  dealDate: string;
  index: number;
}

const ValuationCard: FC<ValuationItemProps> = ({ rotationNumber, location, dealDate, index }) => (
  <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 hover:border-gray-200 dark:hover:border-slate-600 transition-colors">
    {/* Index Badge */}
    <div className="w-8 h-8 rounded-lg bg-vine/10 dark:bg-teal-500/20 flex items-center justify-center flex-shrink-0">
      <span className="text-xs font-bold text-vine dark:text-teal-400">{index}</span>
    </div>

    {/* Info */}
    <div className="flex-1 min-w-0 grid grid-cols-3 gap-2">
      {/* Rotation Number */}
      <div className="flex items-center gap-1.5">
        <svg
          className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
        </svg>
        <span className="text-xs text-gray-700 dark:text-gray-300 truncate" title={rotationNumber}>
          {rotationNumber}
        </span>
      </div>

      {/* Location */}
      <div className="flex items-center gap-1.5">
        <svg
          className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="text-xs text-gray-700 dark:text-gray-300 truncate" title={location}>
          {location}
        </span>
      </div>

      {/* Date */}
      <div className="flex items-center gap-1.5">
        <svg
          className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="text-xs text-gray-700 dark:text-gray-300 truncate" title={dealDate}>
          {dealDate}
        </span>
      </div>
    </div>
  </div>
);

// Valuation data type
interface ValuationData {
  rotationNumber: string;
  location: string;
  dealDate: string;
}

const MarketMetricsSection: FC<MarketMetricsSectionProps> = ({
  productDetails,
  currencyFormatter,
  t,
  isLoading = false,
}) => {
  const [showMetrics, setShowMetrics] = useState(false);

  // Check if we have any quality metrics data
  // Also consider combinedScore (normalizedCriticScore) and market price as available metrics
  const hasQualityMetrics =
    productDetails.grade ||
    productDetails.fairValueScore ||
    productDetails.liquidityScore ||
    productDetails.performanceScore ||
    productDetails.vintageScore ||
    productDetails.combinedScore ||
    productDetails.marketPrice;

  // Show loading state OR actual content
  if (!hasQualityMetrics && !isLoading) {
    return null;
  }

  return (
    <>
      {/* Metrics Button */}
      <div className={`mx-4 mb-4 transition-opacity duration-300 ${isLoading ? 'opacity-50' : 'opacity-100'}`}>
        <button
          type="button"
          onClick={() => !isLoading && setShowMetrics(true)}
          disabled={isLoading}
          className={`w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-vine/10 to-teal-50 dark:from-vine/20 dark:to-teal-900/30 rounded-xl border border-vine/20 dark:border-vine/30 transition-colors ${
            isLoading
              ? 'cursor-not-allowed'
              : 'hover:from-vine/15 hover:to-teal-100 dark:hover:from-vine/25 dark:hover:to-teal-900/40'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-vine flex items-center justify-center shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div className="text-left">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                {t('product:wineDetails.marketMetrics')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('product:wineDetails.qualityAndPerformance')}
              </p>
            </div>
          </div>
          <svg
            className="w-5 h-5 text-gray-400 dark:text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Full Screen Metrics Overlay */}
      {showMetrics && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md mx-4 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-vine/10 to-teal-50 dark:from-vine/20 dark:to-teal-900/30 border-b border-vine/20 dark:border-vine/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-vine flex items-center justify-center shadow-md">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">
                    {t('product:wineDetails.marketMetrics')}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('product:wineDetails.qualityAndPerformance')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowMetrics(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-900 dark:bg-slate-600 hover:bg-gray-800 dark:hover:bg-slate-500 transition-colors shadow-lg"
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-5">
              {/* Radar Chart */}
              <div className="flex justify-center mb-5">
                <QualityRadarChart
                  vintage={productDetails.vintageScore ?? 0}
                  critic={productDetails.combinedScore ?? 0}
                  fairValue={productDetails.fairValueScore ?? 0}
                  liquidity={productDetails.liquidityScore ?? 0}
                  performance={productDetails.performanceScore ?? 0}
                  size={160}
                />
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-3">
                <MetricCard
                  label="Vintage Score"
                  value={productDetails.vintageScore ? `${roundNumber(productDetails.vintageScore, 0)}` : '-'}
                  suffix="/100"
                />
                <MetricCard
                  label="Normalised Critic Score"
                  value={productDetails.combinedScore ? `${roundNumber(productDetails.combinedScore, 0)}` : '-'}
                  suffix="/100"
                />
                <MetricCard
                  label="Fair Value Score"
                  value={productDetails.fairValueScore ? `${roundNumber(productDetails.fairValueScore, 0)}` : '-'}
                  suffix="/100"
                />
                <MetricCard
                  label="Liquidity Score"
                  value={productDetails.liquidityScore ? `${roundNumber(productDetails.liquidityScore, 0)}` : '-'}
                  suffix="/100"
                />
                <MetricCard
                  label="Performance Score"
                  value={productDetails.performanceScore ? `${roundNumber(productDetails.performanceScore, 1)}` : '-'}
                  suffix="/100"
                />
                <MetricCard
                  label="Overall Grade"
                  value={productDetails.grade ? `${roundNumber(productDetails.grade, 1)}` : '-'}
                  suffix="/10"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 bg-gray-50 dark:bg-slate-700 border-t border-gray-100 dark:border-slate-600">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium truncate">
                {`${productDetails.vintage ?? ''} ${productDetails.wineName}`.trim()}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const SellOrInvestProductTemplate: FC<SellOrInvestProductTemplateProps> = ({
  product,
  timestamp,
  onCTA,
  isDetailsFetched = false,
  isLoadingMarket,
  marketOffers,
  isLoadingPortfolioStock,
  portfolioStock,
  isMetricsLoading = false,
}) => {
  const NA = 'n/a';
  const { t } = useTranslation();
  const isProspect = useIsProspect();
  const [isExplainerExpanded, setIsExplainerExpanded] = useState(false);
  const [isTastingNotesExpanded, setIsTastingNotesExpanded] = useState(false);
  const {
    formatter: currencyFormatter,
    state: {
      settings: { currency },
    },
  } = useContext(AppContext);

  const formatter = useMemo(
    () => ({ format: (value: number) => currencyFormatter.format(value, true) }),
    [currencyFormatter],
  );
  const [currentTimestamp, setCurrentTimestamp] = useState<number | undefined>(timestamp);
  const [productDetails, setProductDetails] = useState(product);

  const { data: aiExplainers, isLoading: aiExplainerLoading } = useFetchPortalDocument({
    docType: PortalDocumentTypeEnum.AI_EXPLAINER,
    docRef: product.lwin18.slice(0, 11),
  });

  const { data: currentHoldingsDetailsVT2 } = useQuery({
    queryFn: () => fetchCurrentHoldingDetailsVT2({ lwin18: product.lwin18 }),
    queryKey: ['fetchProductDetails', { lwin18: product.lwin18 }],
    enabled: !isDetailsFetched,
  });

  const slideoutContext = useContext(SortAndFilterLayoutContext);

  // const getProductDetails = async () => {
  //   const { data, error } = await fetchProductDetails({ variables: { id: product.id } });
  //   if (error instanceof Error) {
  //     logError((error as Error).message);
  //   }
  //   return (data as any)?.portalHoldingDetails as Product;
  // };

  // Extract AI explainer text from the response
  const aiExplainerText = useMemo(() => {
    if (!aiExplainers?.data || aiExplainers.data.length === 0) return null;
    // The API returns an array of documents - get the first one
    const explainerDoc = aiExplainers.data[0] as { explainer?: string; content?: string; description?: string };
    // Try different possible field names for the explainer text
    return explainerDoc?.explainer || explainerDoc?.content || explainerDoc?.description || null;
  }, [aiExplainers]);

  // Valuation data for card display
  const valuationData = useMemo((): ValuationData[] => {
    const holdings = productDetails?.holdingStocks || [
      {
        rotationNumber: productDetails?.rotationNumber,
        dealDate: productDetails?.dealDate,
        location: productDetails?.location,
      },
    ];
    return holdings.map((valuation) => ({
      rotationNumber: valuation?.rotationNumber || 'n/a',
      location: valuation?.location || 'n/a',
      dealDate: valuation?.dealDate ? moment(valuation.dealDate).format('DD MMM YYYY') : 'n/a',
    }));
  }, [productDetails]);

  const [buySellModel, setBuySellModel] = useState<BuySellHoldingModel>({ ...defaultModel });
  const [showValuationsModal, setShowValuationsModal] = useState(false);
  const MAX_VISIBLE_ITEMS = 3;
  // Always show max 3 items in the slideout, open modal for more
  const visibleValuations = useMemo(() => valuationData.slice(0, MAX_VISIBLE_ITEMS), [valuationData]);
  const hasMoreValuations = valuationData.length > MAX_VISIBLE_ITEMS;

  const [arrangeDeliveryModel, setArrangeDeliveryModel] = useState<ArrangeDeliveryRequest>({
    lwin18: product.lwin18,
    qty: 0,
    ...blankAddessModel,
  });

  const [viewState, setViewState] = useState(ViewStateType.DEFAULT);
  const {
    wineName,
    totalValue: price,
    changedPct,
    profitAndLoss: valuePnL,
    vintage,
    highestBid,
    lowestOffer,
    physicalQuantity,
  } = product;
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'product:wineDetails', t), [t]);
  const pAndL = `${valuePnL > 0 ? '+' : ''}${formatter.format(valuePnL || 0)} (${
    changedPct > 0 ? '+' : ''
  }${roundNumber(changedPct)}%)`;
  const compConfig = useMemo(() => {
    return {
      costPerUnit: 'avgCost' as keyof Product,
    };
  }, []);

  const marketInfos = useMemo(() => {
    const perUnitText = displayText[DisplayTextKeys.PER_UNIT].toLowerCase();
    const bottom = [
      {
        title: displayText[DisplayTextKeys.AVG_PURCHASE_PRICE],
        value: productDetails?.totalCost || 0,
        unitPrice: productDetails?.[compConfig.costPerUnit] || 0,
        unitPriceSuffix: perUnitText,
        color: InfoColors.COST,
      },
    ];

    return {
      top: [
        {
          title: displayText[DisplayTextKeys.MARKET_PRICE],
          value: productDetails?.totalValue || 0,
          unitPrice: productDetails?.valuePerUnit || 0,
          unitPriceSuffix: perUnitText,
          color: InfoColors.MARKET,
        },
        {
          title: displayText[DisplayTextKeys.AVG_PROFIT_LOSS].trim(),
          value: productDetails?.profitAndLoss || 0,
          unitPrice: productDetails?.avgProfitAndLoss || productDetails.profitAndLoss / productDetails.qty || 0,
          unitPriceSuffix: perUnitText,
          color: InfoColors.P_AND_L,
        },
      ],
      bottom,
      graphInfo: {
        marketValue: productDetails?.totalCost,
        pNlIncludeFees: productDetails?.profitAndLoss || 0,
        pNl: productDetails?.netPosition || 0,
        fees: productDetails?.totalMgmtFee,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetails]);

  useEffect(() => {
    if (timestamp !== currentTimestamp) {
      setCurrentTimestamp(timestamp);
      setViewState(ViewStateType.DEFAULT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timestamp]);

  const onBack = () => {
    slideoutContext?.updateSlideoutConfig({ showBackButton: false });
    setBuySellModel({ ...defaultModel });
    if (onCTA) onCTA(ProductEventType.SET_TITLE, t`portfolio:wineDetails.title`);
    setViewState(ViewStateType.DEFAULT);
  };

  useEffect(() => {
    if (viewState !== ViewStateType.DEFAULT) slideoutContext?.updateSlideoutConfig({ showBackButton: true, onBack });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState]);

  useEffect(() => {
    if (currentHoldingsDetailsVT2 && !isDetailsFetched) {
      // Preserve review data from original product when merging with API response
      setProductDetails({
        ...currentHoldingsDetailsVT2,
        // Keep review data from original product prop (not returned by details API)
        wa: product.wa ?? currentHoldingsDetailsVT2.wa,
        vinous: product.vinous ?? currentHoldingsDetailsVT2.vinous,
        wineAdvocateReview: product.wineAdvocateReview ?? currentHoldingsDetailsVT2.wineAdvocateReview,
        vinousReview: product.vinousReview ?? currentHoldingsDetailsVT2.vinousReview,
        tastingNotes: product.tastingNotes ?? currentHoldingsDetailsVT2.tastingNotes,
      });
    }
  }, [currentHoldingsDetailsVT2, currency, isDetailsFetched, product]);

  // Sync review data and quality metrics from product prop when it changes
  // (e.g., when market insights async data loads)
  // This ensures the sections show data even before currentHoldingsDetailsVT2 loads
  useEffect(() => {
    // Check if product prop has review or quality metrics data
    const hasReviewData =
      product.wineAdvocateReview || product.vinousReview || product.wa || product.vinous || product.tastingNotes;
    const hasQualityMetrics =
      product.grade ||
      product.fairValueScore ||
      product.liquidityScore ||
      product.performanceScore ||
      product.vintageScore ||
      product.combinedScore;

    if (hasReviewData || hasQualityMetrics) {
      setProductDetails((prev) => {
        // Check if we actually need to update
        const needsReviewUpdate =
          (product.wineAdvocateReview && !prev.wineAdvocateReview) ||
          (product.vinousReview && !prev.vinousReview) ||
          (product.wa && !prev.wa) ||
          (product.vinous && !prev.vinous) ||
          (product.tastingNotes && !prev.tastingNotes);

        const needsMetricsUpdate =
          (product.grade && !prev.grade) ||
          (product.fairValueScore && !prev.fairValueScore) ||
          (product.liquidityScore && !prev.liquidityScore) ||
          (product.performanceScore && !prev.performanceScore) ||
          (product.vintageScore && !prev.vintageScore) ||
          (product.combinedScore && !prev.combinedScore) ||
          (product.marketPrice && !prev.marketPrice);

        if (!needsReviewUpdate && !needsMetricsUpdate) return prev;

        return {
          ...prev,
          wa: product.wa ?? prev.wa,
          vinous: product.vinous ?? prev.vinous,
          wineAdvocateReview: product.wineAdvocateReview ?? prev.wineAdvocateReview,
          vinousReview: product.vinousReview ?? prev.vinousReview,
          tastingNotes: product.tastingNotes ?? prev.tastingNotes,
          grade: product.grade ?? prev.grade,
          fairValueScore: product.fairValueScore ?? prev.fairValueScore,
          liquidityScore: product.liquidityScore ?? prev.liquidityScore,
          performanceScore: product.performanceScore ?? prev.performanceScore,
          vintageScore: product.vintageScore ?? prev.vintageScore,
          combinedScore: product.combinedScore ?? prev.combinedScore,
          marketPrice: product.marketPrice ?? prev.marketPrice,
        };
      });
    }
  }, [
    product.wa,
    product.vinous,
    product.wineAdvocateReview,
    product.vinousReview,
    product.tastingNotes,
    product.grade,
    product.fairValueScore,
    product.liquidityScore,
    product.performanceScore,
    product.vintageScore,
    product.combinedScore,
    product.marketPrice,
  ]);

  return (
    <div className="flex flex-col bg-white dark:bg-slate-800">
      {/* Wine Title & AI Explainer */}
      <div className="px-4 pt-4 pb-3">
        <h1 className="text-20 font-semibold text-gray-900 dark:text-white leading-tight">
          {`${vintage ?? ''} ${wineName}`.trim()}
        </h1>

        {/* AI Explainer Section */}
        {aiExplainerLoading && <div className="mt-3 h-16 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />}
        {!aiExplainerLoading && aiExplainerText && (
          <div className="mt-3 bg-gradient-to-br from-vine/5 to-vine/10 dark:from-vine/10 dark:to-vine/20 rounded-xl p-3 border border-vine/10 dark:border-vine/20">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-vine/10 dark:bg-vine/20 flex items-center justify-center flex-shrink-0">
                <AiStarIcon className="text-vine dark:text-teal-300 w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm text-gray-700 dark:text-gray-300 leading-relaxed ${
                    !isExplainerExpanded ? 'line-clamp-3' : ''
                  }`}
                >
                  {aiExplainerText}
                </p>
                {aiExplainerText.length > 150 && (
                  <button
                    onClick={() => setIsExplainerExpanded(!isExplainerExpanded)}
                    className="text-xs text-vine dark:text-teal-300 font-medium mt-1 hover:underline"
                  >
                    {isExplainerExpanded ? t('common:readLess') : t('common:readMore')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {viewState !== ViewStateType.ARRANGE_DELIVERY && viewState !== ViewStateType.ACCEPT_BID && (
        <div className="flex flex-col">
          {/* Value Hero Card */}
          <div className="mx-4 mb-4 bg-gradient-to-br from-vine to-vine/80 rounded-2xl p-4 text-white shadow-lg">
            <div className="text-center">
              <span className="text-xs text-white/70 uppercase tracking-wide">
                {displayText[DisplayTextKeys.TOTAL_VALUE]}
              </span>
              {product.unit && <div className="text-xs text-white/50 mt-0.5">{product.unit}</div>}
              <div className="flex items-center justify-center gap-2 mt-1">
                <span className="text-2xl font-bold">{formatter.format(price)}</span>
                <span
                  className={`text-sm font-medium px-2 py-0.5 rounded-full ${
                    valuePnL >= 0 ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'
                  }`}
                >
                  {pAndL}
                </span>
              </div>
            </div>

            {/* Bid/Offer in hero card */}
            <div className="flex mt-4 pt-3 border-t border-white/20">
              <div className="flex-1 text-center">
                <span className="text-xs text-white/60">{displayText[DisplayTextKeys.HIGHEST_BID]}</span>
                <div className="text-base font-semibold mt-0.5">
                  {highestBid?.priceInTargetCurrency && physicalQuantity > 0
                    ? currencyFormatter.format(highestBid?.priceInTargetCurrency ?? 0, true)
                    : NA}
                </div>
              </div>
              <div className="w-px bg-white/20" />
              <div className="flex-1 text-center">
                <span className="text-xs text-white/60">{displayText[DisplayTextKeys.LOWEST_OFFER]}</span>
                <div className="text-base font-semibold mt-0.5">
                  {lowestOffer?.priceInTargetCurrency
                    ? currencyFormatter.format(lowestOffer?.priceInTargetCurrency ?? 0, true)
                    : NA}
                </div>
              </div>
            </div>
          </div>

          {/* Prospect accounts explore real wines against demonstration holdings,
              so there is nothing to buy or sell against. The API refuses these
              actions regardless - this only keeps us from offering them. */}
          {viewState === ViewStateType.DEFAULT && isProspect && (
            <div className="px-4 mb-4">
              <ProspectTradingNotice />
            </div>
          )}

          {/* Action Buttons */}
          {viewState === ViewStateType.DEFAULT && !isProspect && (
            <div className="px-4 mb-4 space-y-2">
              {isLoadingMarket && <div className="h-12 rounded-full bg-gray-200 dark:bg-slate-700 animate-pulse" />}
              {isLoadingPortfolioStock && (
                <div className="h-12 rounded-full bg-gray-200 dark:bg-slate-700 animate-pulse" />
              )}

              {!isLoadingMarket && (
                <Button
                  isDisable={!marketOffers}
                  onClick={() => {
                    if (onCTA) onCTA(ProductEventType.BUY, '');
                  }}
                  className="btn w-full bg-orange hover:bg-orange/90 rounded-full text-black font-semibold shadow-sm disabled:bg-gray-200 disabled:shadow-none transition-all"
                >
                  {`${displayText[DisplayTextKeys.BUY]} ${
                    product.lowestOffer?.priceInTargetCurrency
                      ? formatter.format(product.lowestOffer?.priceInTargetCurrency ?? 0)
                      : ''
                  }`}
                </Button>
              )}

              {!isLoadingPortfolioStock && (
                <Button
                  isDisable={!portfolioStock}
                  onClick={() => {
                    if (onCTA) onCTA(ProductEventType.MAKE_OFFER, '');
                  }}
                  className="btn w-full bg-vine hover:bg-vine/90 text-white rounded-full font-semibold shadow-sm disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none transition-all"
                >
                  {displayText[DisplayTextKeys.MAKE_OFFER]}
                </Button>
              )}

              <Button
                isDisable={!highestBid?.priceInTargetCurrency || physicalQuantity < 1}
                onClick={() => {
                  if (onCTA) onCTA(ProductEventType.SELL, '');
                }}
                className="btn w-full rounded-full text-vine dark:text-teal-300 border-2 border-vine dark:border-teal-300 hover:bg-vine/5 dark:hover:bg-teal-300/10 font-semibold disabled:border-gray-200 dark:disabled:border-gray-600 disabled:text-gray-400 dark:disabled:text-gray-500 transition-all"
              >
                {`${displayText[DisplayTextKeys.SELL]} ${
                  product.highestBid?.priceInTargetCurrency && physicalQuantity > 0
                    ? formatter.format(product.highestBid?.priceInTargetCurrency ?? 0)
                    : ''
                }`}
              </Button>
            </div>
          )}
        </div>
      )}

      {viewState === ViewStateType.DEFAULT && (
        <>
          {/* Holdings & Valuation Section */}
          <div className="px-4 py-4 bg-gray-50 dark:bg-slate-900">
            <SectionHeader
              title={displayText[DisplayTextKeys.DATA_AND_VALUATION]}
              icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              }
            />

            {/* Column Headers */}
            <div className="mb-2 px-3 py-2 bg-vine dark:bg-slate-700 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-8" /> {/* Spacer for index badge */}
                <div className="flex-1 grid grid-cols-3 gap-2">
                  <span className="text-[10px] font-semibold text-white/90 uppercase tracking-wide">
                    {t('product:dataValuations.tableHeadings.rotation_number')}
                  </span>
                  <span className="text-[10px] font-semibold text-white/90 uppercase tracking-wide">
                    {t('product:dataValuations.tableHeadings.location')}
                  </span>
                  <span className="text-[10px] font-semibold text-white/90 uppercase tracking-wide">
                    {t('product:dataValuations.tableHeadings.purchaseDate')}
                  </span>
                </div>
              </div>
            </div>

            {/* Valuation Cards */}
            <div className="space-y-2">
              {visibleValuations.map((item, index) => (
                <ValuationCard
                  key={`valuation-${index}`}
                  rotationNumber={item.rotationNumber}
                  location={item.location}
                  dealDate={item.dealDate}
                  index={index + 1}
                />
              ))}
            </div>

            {/* View All Button */}
            {hasMoreValuations && (
              <button
                onClick={() => setShowValuationsModal(true)}
                className="w-full mt-2 py-2.5 text-sm font-medium text-white bg-vine dark:bg-teal-600 hover:bg-vine/90 dark:hover:bg-teal-500 rounded-xl transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 10h16M4 14h16M4 18h16"
                  />
                </svg>
                {t('common:viewAll')} ({valuationData.length} {t('common:records')})
              </button>
            )}

            {/* Valuations Modal */}
            {showValuationsModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="w-full max-w-md mx-4 bg-gray-50 dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden max-h-[80vh] flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-vine flex items-center justify-center shadow-md">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-gray-900 dark:text-white">
                          {displayText[DisplayTextKeys.DATA_AND_VALUATION]}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {valuationData.length} {t('common:records')}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowValuationsModal(false)}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-900 dark:bg-slate-600 hover:bg-gray-800 dark:hover:bg-slate-500 transition-colors shadow-lg"
                    >
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Column Headers in Modal */}
                  <div className="px-4 py-2 bg-vine dark:bg-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="w-8" />
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <span className="text-[10px] font-semibold text-white/90 uppercase tracking-wide">
                          {t('product:dataValuations.tableHeadings.rotation_number')}
                        </span>
                        <span className="text-[10px] font-semibold text-white/90 uppercase tracking-wide">
                          {t('product:dataValuations.tableHeadings.location')}
                        </span>
                        <span className="text-[10px] font-semibold text-white/90 uppercase tracking-wide">
                          {t('product:dataValuations.tableHeadings.purchaseDate')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Content - scrollable */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {valuationData.map((item, index) => (
                      <ValuationCard
                        key={`modal-valuation-${index}`}
                        rotationNumber={item.rotationNumber}
                        location={item.location}
                        dealDate={item.dealDate}
                        index={index + 1}
                      />
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-3 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium truncate">
                      {`${vintage ?? ''} ${wineName}`.trim()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Value Breakdown Cards */}
            <div className="mt-4 space-y-3">
              <div className="flex gap-2">
                {marketInfos.top.map((item, index) => (
                  <StatCard
                    key={`${item.title}-${index}`}
                    label={item.title}
                    value={formatter.format(item.value)}
                    subValue={`${formatter.format(item.unitPrice)} ${item.unitPriceSuffix}`}
                    color={item.color}
                    trend={item.title.includes('Profit') ? (item.value >= 0 ? 'up' : 'down') : undefined}
                  />
                ))}
              </div>

              <MarketInfoGraph includeFees={false} data={marketInfos.graphInfo} />

              <div className="flex gap-2">
                {marketInfos.bottom.map((item, index) => (
                  <StatCard
                    key={`${item.title}-${index}`}
                    label={item.title}
                    value={formatter.format(item.value)}
                    subValue={`${formatter.format(item.unitPrice as number)} ${item.unitPriceSuffix}`}
                    color={item.color}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Market Price Chart Section */}
          <div className="px-4 py-4">
            <SectionHeader
              title={displayText[DisplayTextKeys.LIVE_EX_CHART_TITLE]}
              icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                  />
                </svg>
              }
            />
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm">
              <LiveExPerformanceChart chartData={productDetails?.historicMarketPrices || []} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
              {displayText[DisplayTextKeys.LIVE_EX_PERFORMANCE_CHART_DESCRIPTION]}
            </p>
          </div>

          {/* Tasting Notes Section - Multiple Reviewers */}
          <TastingNotesSection
            productDetails={productDetails}
            t={t}
            isTastingNotesExpanded={isTastingNotesExpanded}
            setIsTastingNotesExpanded={setIsTastingNotesExpanded}
          />

          {/* Market Metrics Section */}
          <MarketMetricsSection
            productDetails={productDetails}
            currencyFormatter={currencyFormatter}
            t={t}
            isLoading={isMetricsLoading}
          />

          {/* Arrange Delivery CTA */}
          <div className="px-4 py-4">
            <div className="relative bg-gradient-to-r from-gray-100 to-gray-50 dark:from-slate-700 dark:to-slate-600 rounded-2xl p-4 overflow-hidden">
              <img
                src={cutlWinesLoversImage}
                className="absolute right-0 top-0 h-full w-auto opacity-90"
                alt="wine lovers"
              />
              <div className="relative z-10 max-w-[60%]">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                  {displayText[DisplayTextKeys.ARRANGE_DELIVERY_TITLE]}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {displayText[DisplayTextKeys.ARRANGE_DELIVERY_SUBTITLE]}
                </p>
                <Button
                  className="btn text-sm font-semibold bg-orange hover:bg-orange/90 rounded-full mt-3 text-black shadow-sm px-4 py-2"
                  onClick={() => {
                    if (onCTA) onCTA(ProductEventType.SET_TITLE, displayText[DisplayTextKeys.ARRANGE_DELIVERY_TITLE]);
                    setViewState(ViewStateType.ARRANGE_DELIVERY);
                  }}
                  props={{
                    name: DisplayTextKeys.ARRANGE_DELIVERY_BUTTON_TEXT,
                  }}
                >
                  {displayText[DisplayTextKeys.ARRANGE_DELIVERY_BUTTON_TEXT]}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {viewState === ViewStateType.INVEST_MORE && (
        <InvestMoreTemplate
          formatter={formatter}
          onCTA={onCTA}
          lwin18={product.lwin18}
          marketPrice={product.valuePerUnit}
          model={buySellModel}
          qtyOwned={product.qty || 0}
          setModel={(modelIn) => setBuySellModel(modelIn)}
        />
      )}

      {viewState === ViewStateType.SELL_HOLDINGS && (
        <SellHoldingTemplate
          formatter={formatter}
          onCTA={onCTA}
          lwin18={product.lwin18}
          marketPrice={product.valuePerUnit}
          model={buySellModel}
          qtyOwned={product.qty || 0}
          setModel={(modelIn) => setBuySellModel(modelIn)}
        />
      )}

      {viewState === ViewStateType.ARRANGE_DELIVERY && (
        <ArrangeDeliveryTemplate
          qtyOwned={product.qty || 0}
          onCTA={onCTA}
          model={arrangeDeliveryModel}
          setModel={(modelIn) => setArrangeDeliveryModel(modelIn)}
        />
      )}
    </div>
  );
};

export default SellOrInvestProductTemplate;
